import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import WmsApp from "./wms/WmsApp";
import ProdApp from "./production/ProdApp";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Search, Package, Users, FileText, LogOut, Plus, Upload, Download, Edit, Trash2, Menu, X, Grid, Check, CheckCircle2, Image as ImageIcon, Save, FileSpreadsheet, Settings, Eye, EyeOff, GripVertical, Languages, ChevronDown, Bell, Calculator, ShoppingCart, Minus, ShoppingBag, Mail, Phone, MapPin, Building2, ClipboardList, ChevronRight, UserCog, BarChart3, MessageCircle, Sparkles, Receipt, Banknote, Video, FolderOpen, PanelLeftClose, PanelLeftOpen, Wallet, PackagePlus, Boxes, ArrowRightLeft, ExternalLink, MonitorDown, Truck, Gauge } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Switch } from "./components/ui/switch";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";
import { Checkbox } from "./components/ui/checkbox";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AgentPortal from "./portals/AgentPortal";
import GdprGate from "./components/GdprGate";
import WebmailPage from "./components/WebmailPage";
import WarehousePortal from "./portals/WarehousePortal";
import PurchasesPortal from "./purchases/PurchasesPortal";
import CustomerPortal from "./portals/CustomerPortal";
import AdminBIDashboard from "./components/AdminBIDashboard";
import AdminKpiDashboard from "./components/AdminKpiDashboard";
import AgentCommissionsAdmin from "./components/AgentCommissionsAdmin";
import NumberInput from "./components/NumberInput";
import { ChatButton, ChatDrawer } from "./components/ChatDrawer";
import { SuppliersTab, SupplierOrdersTab } from "./components/SuppliersComponents";
import { LangProvider, useLang } from "./context/LangContext";
import { CompanyProvider, useCompany } from "./context/CompanyContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import { formatMoney, currencySymbol } from "./utils/currency";
import { calculateBasePrice, calculateFinalPrice, getWeekNumber, getLocalizedName, getLocalizedDesc } from "./utils/format";
import { Logo } from "./components/Logo";
import PublicPortal, { ProductDetailModal } from "./pages/PublicPortal";
import OrdersManagement from "./pages/admin/OrdersManagement";
import ListinoGenerator from "./pages/admin/ListinoGenerator";
import ProductsManagement from "./pages/admin/ProductsManagement";
import PricelistTypesManagement from "./pages/admin/PricelistTypesManagement";
import CategoriesManagement from "./pages/admin/CategoriesManagement";
import AgentsManagement from "./pages/admin/AgentsManagement";
import AdminCustomersManagement from "./pages/admin/CustomersManagement";
import SettingsPage from "./pages/admin/SettingsPage";
import DedicatedOffersList from "./components/DedicatedOffersList";
import UsersManagement from "./pages/admin/UsersManagement";
import PaymentTermsManagement from "./pages/admin/PaymentTermsManagement";
import StatementsPage from "./pages/StatementsPage";
import DocumentsPage from "./pages/DocumentsPage";
import TutorialsPage from "./pages/TutorialsPage";
import InventoryShipments from "./pages/warehouse/InventoryShipments";
import InventoryLots from "./pages/warehouse/InventoryLots";
import EtifoodSales from "./pages/admin/EtifoodSales";
import EtifoodAgent from "./pages/admin/EtifoodAgent";
import EtifoodDDT from "./pages/admin/EtifoodDDT";
import LogisticsPage from "./pages/LogisticsPage";
import WmsGiacenze from "./pages/wms/WmsGiacenze";
import WmsMovimenti from "./pages/wms/WmsMovimenti";
import { useRealtime } from "./hooks/useRealtime";
import { LoginChooser, RoleLogin } from "./pages/LoginPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ AUTH ============
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  // Superadmin can switch the active company at runtime via X-Company-Id header
  const [activeCompanyId, setActiveCompanyId] = useState(localStorage.getItem("active_company_id"));
  const [availableCompanies, setAvailableCompanies] = useState(() => {
    try { return JSON.parse(localStorage.getItem("available_companies") || "[]"); } catch { return []; }
  });

  // Install a global axios interceptor that injects X-Company-Id when set
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      const cid = localStorage.getItem("active_company_id");
      if (cid) {
        config.headers = config.headers || {};
        config.headers["X-Company-Id"] = cid;
      }
      return config;
    });
    return () => { axios.interceptors.request.eject(id); };
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      const isSA = response.data.role === "superadmin" || response.data.is_superadmin === true;
      if (isSA && response.data.available_companies) {
        setAvailableCompanies(response.data.available_companies);
        localStorage.setItem("available_companies", JSON.stringify(response.data.available_companies));
      }
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = (companyId) => {
    if (!companyId) {
      localStorage.removeItem("active_company_id");
      setActiveCompanyId(null);
    } else {
      localStorage.setItem("active_company_id", companyId);
      setActiveCompanyId(companyId);
    }
    // Force reload so all data refetches with new scope
    window.location.reload();
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("impersonating");
    localStorage.removeItem("impersonating_name");
    localStorage.removeItem("active_company_id");
    localStorage.removeItem("available_companies");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setActiveCompanyId(null);
    setAvailableCompanies([]);
  };

  return <AuthContext.Provider value={{
    user, token, login, logout, loading,
    activeCompanyId, availableCompanies, setAvailableCompanies, switchCompany,
    isImpersonating: !!localStorage.getItem("impersonating"),
    impersonatingName: localStorage.getItem("impersonating_name"),
  }}>{children}</AuthContext.Provider>;
};

const ImpersonationBanner = () => {
  const { isImpersonating, impersonatingName, logout } = useAuth();
  const navigate = useNavigate();
  if (!isImpersonating) return null;
  return (
    <div className="bg-amber-500 text-white px-4 py-2 text-sm font-semibold flex items-center justify-between sticky top-0 z-[60]" data-testid="impersonation-banner">
      <span>👁️ Stai visualizzando come <strong>{impersonatingName}</strong></span>
      <button onClick={() => { logout(); navigate("/login"); }} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs" data-testid="exit-impersonation-btn">Esci e torna admin</button>
    </div>
  );
};

// ============ LANG CONTEXT ============
// LangProvider/useLang moved to ./context/LangContext.js
// Logo/calculateBasePrice/calculateFinalPrice/getWeekNumber/getLocalizedName/getLocalizedDesc imported above

// ============ LOGIN ============

// ============ SIDEBAR ============
const AdminSidebar = ({ activeTab, setActiveTab, collapsed, onToggleCollapsed }) => {
  const { user, logout, activeCompanyId, availableCompanies, switchCompany } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const isSuperAdmin = user?.is_superadmin === true || user?.role === "superadmin";
  // Effective company shown to the user (overridden by superadmin's selection)
  const effectiveCompanyId = activeCompanyId || user?.company_id;

  const menuGroups = [
    {
      id: "dashboard",
      label: "Dashboard",
      items: [
        { id: "bi", label: "Dashboard BI", icon: BarChart3 },
        { id: "kpi", label: "KPI aziendali", icon: Gauge },
        { id: "etifood-sales", label: "Import DDT Etifood", icon: Receipt },
        { id: "etifood-agent", label: "Sync Etifood (PC)", icon: MonitorDown },
        { id: "etifood-ddt", label: "DDT da Etiflor", icon: Truck },
      ],
    },
    {
      id: "catalog",
      label: "Catalogo",
      items: [
        { id: "products", label: t.products, icon: Package },
        { id: "categories", label: t.categories, icon: Grid },
        { id: "pricelist-types", label: t.pricelists, icon: FileText },
        { id: "generator", label: t.generator, icon: FileSpreadsheet },
      ],
    },
    {
      id: "registry",
      label: "Anagrafiche",
      items: [
        { id: "customers", label: t.customers, icon: Building2 },
        { id: "agents", label: t.agents, icon: Users },
        { id: "users", label: "Utenti & Accessi", icon: UserCog },
        { id: "suppliers", label: t.suppliers, icon: Building2 },
      ],
    },
    {
      id: "operations",
      label: "Operatività",
      items: [
        { id: "orders", label: t.orders, icon: ClipboardList },
        { id: "mail", label: "Mail", icon: Mail },
        { id: "documents", label: "Documenti", icon: FolderOpen },
        { id: "statements", label: "Scadenziario", icon: Receipt },
        { id: "agent-commissions", label: "Estratto Agenti", icon: Wallet },
        { id: "purchases", label: "Acquisti", icon: ShoppingCart, isLink: "/purchases/suppliers" },
        { id: "dedicated-offers", label: "Offerte Dedicate", icon: Sparkles },
        { id: "logistica", label: "Logistica", icon: Truck },
        { id: "new-order", label: t.newOrder || "Nuovo ordine", icon: ShoppingCart, isLink: "/agent/new-order" },
      ],
    },
    {
      id: "warehouse-inventory",
      label: "Magazzino",
      items: [
        { id: "wh-shipments", label: "Carichi Magazzino", icon: PackagePlus },
        { id: "wh-lots", label: "Giacenze & Partite", icon: Boxes },
      ],
    },
    {
      id: "wms",
      label: "WMS",
      items: [
        { id: "wms-giacenze", label: "Giacenze & Tracciabilità", icon: Boxes },
        { id: "wms-movimenti", label: "Movimenti", icon: ArrowRightLeft },
      ],
    },
    {
      id: "system",
      label: "Sistema",
      items: [
        { id: "payment-terms", label: "Termini Pagamento", icon: Banknote },
        { id: "tutorials", label: "Guide & Tutorial", icon: Video },
        { id: "settings", label: t.settings || "Impostazioni", icon: Settings },
      ],
    },
  ];
  // Flat array used for active-tab lookup (id-only)
  const menuItems = menuGroups.flatMap(g => g.items);

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-stone-200 shrink-0 relative">
        {onToggleCollapsed && (
          <button onClick={onToggleCollapsed} className="hidden lg:flex absolute top-3 right-2 p-1.5 rounded-md text-stone-400 hover:text-stone-700 hover:bg-stone-100" title="Comprimi menu" data-testid="sidebar-collapse-btn">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
        <Logo className="h-10" />
        {effectiveCompanyId && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#8A1C2A]/5 border border-[#8A1C2A]/15" data-testid="company-badge">
            <span className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
              {effectiveCompanyId === "giotto-polska" ? "🇵🇱 Polska" : "🇮🇹 Italia"}
            </span>
            {isSuperAdmin && <span className="text-[9px] uppercase font-semibold text-[#C5A059] ml-2 px-1.5 py-0.5 bg-[#C5A059]/10 rounded border border-[#C5A059]/30">SUPER</span>}
          </div>
        )}
        {/* Superadmin company switcher */}
        {isSuperAdmin && availableCompanies.length > 1 && (
          <div className="mt-3" data-testid="superadmin-company-switcher">
            <label className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">{t.changeCompany}</label>
            <Select value={effectiveCompanyId} onValueChange={(v) => switchCompany(v)}>
              <SelectTrigger className="h-9 mt-1 bg-white border-[#C5A059]/40 text-sm" data-testid="superadmin-company-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableCompanies.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>
                    {c.id === "giotto-polska" ? "🇵🇱" : "🇮🇹"} {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4 min-h-0">
        {menuGroups.map((group) => (
          <div key={group.id} className="space-y-0.5">
            <p className="px-3 mb-1 text-[10px] uppercase tracking-wider font-bold text-stone-400">{group.label}</p>
            {group.items.map((item) => (
              <button key={item.id} onClick={() => {
                if (item.isLink) { navigate(item.isLink); setIsMobileOpen(false); return; }
                setActiveTab(item.id); setIsMobileOpen(false);
              }}
                className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${(activeTab === item.id && !item.isLink) ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-700 hover:text-stone-900 hover:bg-stone-100"}`}
                data-testid={`sidebar-${item.id}`}>
                <item.icon className="w-4.5 h-4.5 shrink-0" />
                <span className="truncate">{item.label}</span>
                <span
                  role="button"
                  title="Apri in un'altra scheda"
                  onClick={(e) => { e.stopPropagation(); window.open(item.isLink || `/admin?tab=${item.id}`, "_blank"); }}
                  className={`ml-auto p-1 rounded opacity-50 lg:opacity-0 lg:group-hover:opacity-60 hover:!opacity-100 transition-opacity ${(activeTab === item.id && !item.isLink) ? "hover:bg-white/20" : "hover:bg-stone-200"}`}
                  data-testid={`admin-newtab-${item.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-stone-200 space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))] shrink-0">
        {/* Language Switcher */}
        <div className="flex items-center gap-2 px-2">
          <Languages className="w-4 h-4 text-stone-500" />
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-8 bg-white border-stone-200 text-stone-800 text-xs" data-testid="lang-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="it">🇮🇹 Italiano</SelectItem>
              <SelectItem value="en">🇬🇧 English</SelectItem>
              <SelectItem value="pl">🇵🇱 Polski</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Separator className="bg-stone-200" />
        <div className="flex items-center gap-3 px-2">
          <div className="w-8 h-8 rounded-full bg-[#8A1C2A] flex items-center justify-center text-white text-sm font-medium">{user?.name?.[0]?.toUpperCase() || "A"}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
            <p className="text-xs text-stone-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-md" data-testid="logout-btn">
          <LogOut className="w-4 h-4" />{t.logout}
        </button>
      </div>
    </>
  );

  const CompactSidebarContent = () => (
    <>
      <div className="p-3 border-b border-stone-200 flex flex-col items-center gap-1.5 shrink-0">
        <button onClick={onToggleCollapsed} className="p-2 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-100" title="Espandi menu" data-testid="sidebar-expand-btn">
          <PanelLeftOpen className="w-5 h-5" />
        </button>
        <span className="text-sm" title={effectiveCompanyId}>{effectiveCompanyId === "giotto-polska" ? "🇵🇱" : "🇮🇹"}</span>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1 min-h-0">
        {menuItems.map((item) => (
          <button key={item.id} onClick={() => { if (item.isLink) { navigate(item.isLink); return; } setActiveTab(item.id); }}
            className={`w-full flex items-center justify-center p-2.5 rounded-lg transition-all ${(activeTab === item.id && !item.isLink) ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"}`}
            title={item.label}
            data-testid={`sidebar-collapsed-${item.id}`}>
            <item.icon className="w-5 h-5" />
          </button>
        ))}
      </nav>
      <div className="p-2 border-t border-stone-200 flex flex-col items-center gap-1 pb-[max(0.5rem,env(safe-area-inset-bottom))] shrink-0">
        <div className="w-8 h-8 rounded-full bg-[#8A1C2A] flex items-center justify-center text-white text-sm font-medium" title={`${user?.name} · ${user?.email}`}>{user?.name?.[0]?.toUpperCase() || "A"}</div>
        <button onClick={() => { logout(); navigate("/login"); }} className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-md" title={t.logout} data-testid="logout-btn-collapsed">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </>
  );

  return (
    <>
      <button onClick={() => setIsMobileOpen(!isMobileOpen)} className="fixed top-4 left-4 z-50 lg:hidden p-2 bg-white border border-stone-200 rounded-md shadow-sm">
        {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      {isMobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-white border-r border-stone-200 flex flex-col"><SidebarContent /></div>
        </div>
      )}
      <div className={`hidden lg:flex fixed left-0 top-0 bottom-0 ${collapsed ? "w-[64px]" : "w-64"} bg-white border-r border-stone-200 flex-col shadow-sm transition-[width] duration-200`}>
        {collapsed ? <CompactSidebarContent /> : <SidebarContent />}
      </div>
    </>
  );
};

// ============ SORTABLE PRODUCT ROW ============
// ============ PRODUCTS MANAGEMENT ============
// ============ AGENTS MANAGEMENT ============
// ============ PENDING PRODUCTS APPROVAL (admin reviews warehouse proposals) ============
// ============ ADMIN CUSTOMERS MANAGEMENT (admin sees all customers, all agents) ============
// ============ STATS PRODOTTI (drill-down) ============
// ============ ADMIN DASHBOARD ============
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("bi");
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("giotto_sidebar_collapsed") === "1");
  const toggleSidebar = () => setSidebarCollapsed((c) => { localStorage.setItem("giotto_sidebar_collapsed", c ? "0" : "1"); return !c; });
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  useEffect(() => {
    if (user?.company_id) {
      axios.get(`${API}/companies/${user.company_id}`).then(r => setCompany(r.data)).catch(() => {});
    }
  }, [user?.company_id]);

  // Realtime: toast on new orders so admin sees them as they come in
  useRealtime((evt) => {
    if (evt?.type === "order_new") {
      toast.success(`📦 ${evt.order_number} — ${evt.customer_name || "Nuovo ordine"}`, {
        description: "Nuovo ordine ricevuto",
        duration: 6000,
      });
    }
  });

  // Read ?tab=... from URL to support deep-links from chat / external pages
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const validTabs = ["bi", "kpi", "generator", "products", "categories", "pricelist-types", "agents", "customers", "orders", "mail", "dedicated-offers", "stats-products", "suppliers", "supplier-orders", "users", "settings", "payment-terms", "statements", "agent-commissions", "documents", "tutorials", "wh-shipments", "wh-lots", "etifood-sales", "etifood-agent", "etifood-ddt", "wms-giacenze", "wms-movimenti", "logistica"];
    if (tab === "stats-products") { navigate("/admin?tab=bi", { replace: true }); return; }
    if (tab && validTabs.includes(tab)) setActiveTab(tab);
  }, [location.search, navigate]);

  return (
    <div className="min-h-screen bg-stone-50">
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} collapsed={sidebarCollapsed} onToggleCollapsed={toggleSidebar} />
      <div className={sidebarCollapsed ? "lg:pl-[64px]" : "lg:pl-64"}>
        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-stone-200 px-4 lg:px-6 py-2 flex items-center justify-end gap-2">
          <ChatButton onClick={() => setChatOpen(true)} />
        </div>
        <div className="p-6 lg:p-8">
          {activeTab === "bi" && <AdminBIDashboard onNavigate={(tab) => navigate(`/admin?tab=${tab}`)} />}
          {activeTab === "kpi" && <AdminKpiDashboard />}
          {activeTab === "generator" && <ListinoGenerator />}
          {activeTab === "products" && <ProductsManagement />}
          {activeTab === "categories" && <CategoriesManagement />}
          {activeTab === "pricelist-types" && <PricelistTypesManagement />}
          {activeTab === "agents" && <AgentsManagement />}
          {activeTab === "customers" && <AdminCustomersManagement />}
          {activeTab === "orders" && <OrdersManagement />}
          {activeTab === "mail" && <WebmailPage />}
          {activeTab === "dedicated-offers" && <DedicatedOffersList currentUser={user} />}
          {activeTab === "users" && <UsersManagement />}
          {activeTab === "suppliers" && <SuppliersTab canEdit={true} canDelete={true} />}
          {activeTab === "supplier-orders" && <Navigate to="/purchases/orders" replace />}
          {activeTab === "payment-terms" && <PaymentTermsManagement />}
          {activeTab === "statements" && <StatementsPage currentUser={user} company={company} />}
          {activeTab === "agent-commissions" && <AgentCommissionsAdmin company={company} />}
          {activeTab === "documents" && <DocumentsPage currentUser={user} company={company} />}
          {activeTab === "wh-shipments" && <InventoryShipments />}
          {activeTab === "wh-lots" && <InventoryLots />}
          {activeTab === "etifood-sales" && <EtifoodSales />}
          {activeTab === "etifood-agent" && <EtifoodAgent />}
          {activeTab === "etifood-ddt" && <EtifoodDDT />}
          {activeTab === "logistica" && <LogisticsPage canManage />}
          {activeTab === "wms-giacenze" && <WmsGiacenze />}
          {activeTab === "wms-movimenti" && <WmsMovimenti />}
          {activeTab === "tutorials" && <TutorialsPage currentUser={user} />}
          {activeTab === "settings" && <SettingsPage />}
        </div>
      </div>
      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="spinner w-8 h-8" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome(user.role)} />;
  return <GdprGate>{children}</GdprGate>;
};

const roleHome = (role) => ({
  admin: "/admin",
  superadmin: "/admin",
  agent: "/agent/dashboard",
  warehouse: "/warehouse/orders",
  purchasing: "/purchases/suppliers",
  production: "/produzione",
  customer: "/customer/orders",
}[role] || "/login");

const RoleRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="spinner w-8 h-8" /></div>;
  if (!user) return <Navigate to="/login" />;
  return <Navigate to={roleHome(user.role)} />;
};

const AgentPortalWrapper = () => {
  const { user, logout } = useAuth();
  return <AgentPortal user={user} logout={logout} />;
};
const WarehousePortalWrapper = () => {
  const { user, logout } = useAuth();
  return <WarehousePortal user={user} logout={logout} />;
};
const CustomerPortalWrapper = () => {
  const { user, logout } = useAuth();
  return <CustomerPortal user={user} logout={logout} />;
};

const PurchasesPortalWrapper = () => {
  const { user, logout } = useAuth();
  return <PurchasesPortal user={user} logout={logout} />;
};

const AuthenticatedRealtime = ({ children }) => {
  const { token, user } = useAuth();
  return <RealtimeProvider token={user ? token : null}>{children}</RealtimeProvider>;
};

// Tenant-scoped login pages. Preset company + lang AND render the chooser directly.
const TenantLoginPage = ({ companyId, lang, basePath }) => {
  React.useEffect(() => {
    if (companyId) localStorage.setItem("preferred_company_id", companyId);
    if (lang) localStorage.setItem("app_lang", lang);
  }, [companyId, lang]);
  return <LoginChooser tenantHint={companyId} basePath={basePath} />;
};

// Login di ruolo con azienda bloccata dall'URL (es. /it/login/agent)
const TenantRoleLogin = ({ companyId, lang }) => {
  const { role } = useParams();
  React.useEffect(() => {
    if (companyId) localStorage.setItem("preferred_company_id", companyId);
    if (lang) localStorage.setItem("app_lang", lang);
  }, [companyId, lang]);
  if (!["admin", "agent", "warehouse", "customer", "purchasing"].includes(role)) return <Navigate to="/login" replace />;
  return <RoleLogin role={role} lockedCompanyId={companyId} />;
};

function App() {
  // Sottodomini dedicati: agenti.* → login agente, clienti.* → login cliente
  // (basta puntare il DNS del sottodominio alla stessa app)
  React.useEffect(() => {
    const h = window.location.hostname;
    const p = window.location.pathname;
    if (p === "/" || p === "/login") {
      if (h.startsWith("agenti.")) window.location.replace("/login/agent");
      else if (h.startsWith("clienti.")) window.location.replace("/login/customer");
    }
  }, []);
  return (
    <LangProvider>
      <AuthProvider>
        <CompanyProvider>
        <AuthenticatedRealtime>
        <BrowserRouter>
          <ImpersonationBanner />
          <Routes>
            <Route path="/login" element={<LoginChooser />} />
            <Route path="/login/admin" element={<RoleLogin role="admin" />} />
            <Route path="/login/agent" element={<RoleLogin role="agent" />} />
            <Route path="/login/warehouse" element={<RoleLogin role="warehouse" />} />
            <Route path="/login/purchasing" element={<RoleLogin role="purchasing" />} />
            <Route path="/login/customer" element={<RoleLogin role="customer" />} />
            <Route path="/super-access" element={<RoleLogin role="superadmin" />} />
            <Route path="/login/super" element={<Navigate to="/super-access" replace />} />
            <Route path="/giotto-it" element={<TenantLoginPage companyId="giotto-italia" lang="it" />} />
            <Route path="/giotto-pl" element={<TenantLoginPage companyId="giotto-polska" lang="pl" />} />
            <Route path="/it/login" element={<TenantLoginPage companyId="giotto-italia" lang="it" basePath="/it/login" />} />
            <Route path="/pl/login" element={<TenantLoginPage companyId="giotto-polska" lang="pl" basePath="/pl/login" />} />
            <Route path="/it/login/:role" element={<TenantRoleLogin companyId="giotto-italia" lang="it" />} />
            <Route path="/pl/login/:role" element={<TenantRoleLogin companyId="giotto-polska" lang="pl" />} />
            <Route path="/portal/:accessCode" element={<PublicPortal />} />
            <Route path="/admin/*" element={<ProtectedRoute roles={["admin", "superadmin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/agent/*" element={<ProtectedRoute roles={["agent", "admin"]}><AgentPortalWrapper /></ProtectedRoute>} />
            <Route path="/warehouse/*" element={<ProtectedRoute roles={["warehouse", "admin"]}><WarehousePortalWrapper /></ProtectedRoute>} />
            <Route path="/purchases/*" element={<ProtectedRoute><PurchasesPortalWrapper /></ProtectedRoute>} />
            <Route path="/customer/*" element={<ProtectedRoute roles={["customer", "admin"]}><CustomerPortalWrapper /></ProtectedRoute>} />
            <Route path="/wms/*" element={<WmsApp />} />
            <Route path="/produzione/*" element={<ProdApp />} />
            <Route path="/" element={<RoleRedirect />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </AuthenticatedRealtime>
        </CompanyProvider>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
